import { useFetchPapers, usePapers } from '@/stores/appStore';
import { Paper } from '@/stores/slices/paperSlice';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTrace } from './useTrace';

const MAX_POLL_COUNT = 72;
const POLL_INTERVAL_TIME = 10000;

type UsePollingOptions = {
  maxPollCount?: number;
  pollIntervalTime?: number;
};

interface UsePollingReturns {
  restartPolling: (immediately?: boolean) => void;
  startPolling: (immediately?: boolean) => void;
  stopPolling: () => void;
  endPolling: () => void;
  isPolling: boolean;
  pollCount: number;
}

export const useFetchPapersPolling = (subjectId: string, options?: UsePollingOptions): UsePollingReturns => {
  const { warn: traceWarn } = useTrace();
  const {
    maxPollCount = MAX_POLL_COUNT,
    pollIntervalTime = POLL_INTERVAL_TIME,
  } = options || {};

  const [pollCount, setPollCount] = useState<number>(0);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const papers = usePapers(subjectId);
  const { fetchPapers } = useFetchPapers();

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef<number>(0);
  const hasReportedTimeoutRef = useRef<boolean>(false);
  const isMountedRef = useRef(true);
  const papersRef = useRef(papers);
  const stableFetchPapers = useRef(fetchPapers);
  const stableTraceWarn = useRef(traceWarn);

  papersRef.current = papers;

  useEffect(() => {
    stableFetchPapers.current = fetchPapers;
    stableTraceWarn.current = traceWarn;
  }, [fetchPapers, traceWarn]);

  const checkPaperStatus = useCallback(({ information, understands = {}, extractStatus }: Paper) => {
    if (extractStatus! < 0) return true;
    if ([0, 1].includes(information.status)) return false;

    for (const key in understands || {}) {
      if (understands[key].status === 0 || understands[key].status === 1) {
        return false;
      }
    }
    return true;
  }, []);

  const checkAllDone = useCallback((papersToCheck: Paper[]) => {
    return papersToCheck.every(checkPaperStatus);
  }, [checkPaperStatus]);

  const filterUnDone = useCallback((papersToCheck: Paper[]) => {
    return papersToCheck.filter(paper => !checkPaperStatus(paper));
  }, [checkPaperStatus]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const checkAndFetch = useCallback(() => {
    // 使用 ref 确保闭包读取到的计数是最新值
    if (pollCountRef.current >= maxPollCount) {
      stopPolling();
      if (!hasReportedTimeoutRef.current) {
        hasReportedTimeoutRef.current = true;
        stableTraceWarn.current?.({ papers: filterUnDone(papersRef.current) });
      }
      return;
    }

    const allDone = checkAllDone(papersRef.current);
    if (allDone) {
      stopPolling();
      return;
    }

    setPollCount(prev => {
      const next = prev + 1;
      pollCountRef.current = next;
      return next;
    });
    stableFetchPapers.current(subjectId, true);
  }, [maxPollCount, checkAllDone, subjectId, stopPolling, filterUnDone]);

  const startPolling = useCallback((immediately = true) => {
    if (pollIntervalRef.current) return;

    if (immediately) {
      checkAndFetch();
    }

    pollIntervalRef.current = setInterval(checkAndFetch, pollIntervalTime);
    setIsPolling(true);
  }, [checkAndFetch, pollIntervalTime]);

  const endPolling = useCallback(() => {
    stopPolling();
    setPollCount(0);
    pollCountRef.current = 0;
    hasReportedTimeoutRef.current = false;
  }, [stopPolling]);

  const restartPolling = useCallback((immediately = true) => {
    console.log('restart')
    stopPolling();
    setPollCount(0);
    pollCountRef.current = 0;
    hasReportedTimeoutRef.current = false;

    requestAnimationFrame(() => {
      if (isMountedRef.current) {
        startPolling(immediately);
      }
    });
  }, [stopPolling, startPolling]);

  // 关键修复：监听 papers 变化
  useEffect(() => {
    // 如果当前没有在轮询，但 papers 中有未完成的任务，则启动轮询
    if (!isPolling && papers.length > 0 && !checkAllDone(papers)) {
      startPolling(true);
    }

    // 如果正在轮询，但所有 papers 都已完成，则停止轮询
    if (isPolling && papers.length > 0 && checkAllDone(papers)) {
      stopPolling();
    }
  }, [papers, isPolling, checkAllDone, startPolling, stopPolling]);

  useEffect(() => {
    isMountedRef.current = true;
    pollCountRef.current = pollCount;
    hasReportedTimeoutRef.current = false;

    // 初始启动轮询 - 只在挂载时执行一次
    if (papers.length > 0 && !checkAllDone(papers)) {
      startPolling(false);
    }

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组，只在挂载和卸载时执行

  return {
    restartPolling,
    startPolling,
    stopPolling,
    endPolling,
    isPolling,
    pollCount,
  }
};